import { useEffect, useRef } from 'react'

const adoptEmptyField = (current, domValue, setValue) => {
	if (current) return
	const next = String(domValue || '')
	if (!next) return
	setValue(next)
}

const syncBindings = bindings => {
	bindings.forEach(({ ref, value, setValue }) => {
		adoptEmptyField(value, ref.current?.value, setValue)
	})
}

const scheduleRetries = (fn, delays) =>
	delays.map(delay => window.setTimeout(fn, delay))

/**
 * Sync Chrome/password-manager autofill into controlled React state.
 * Autofill often paints the field and sets the DOM value without an input
 * event (and sometimes after the first paint), so buttons stay disabled until
 * focus. Poll + autofill animation + focus/input/change catch those cases.
 */
export const useDomAutofill = bindings => {
	const bindingsRef = useRef(bindings)
	bindingsRef.current = bindings

	useEffect(() => {
		const apply = () => syncBindings(bindingsRef.current)

		apply()
		const timers = scheduleRetries(apply, [0, 50, 100, 200, 400, 800, 1500, 2500, 4000])
		const intervalId = window.setInterval(apply, 250)
		const stopIntervalId = window.setTimeout(
			() => window.clearInterval(intervalId),
			5000,
		)

		const onDomEvent = event => {
			const match = bindingsRef.current.find(
				item => item.ref.current === event.target,
			)
			if (!match) return
			adoptEmptyField(
				bindingsRef.current.find(item => item.ref.current === event.target)
					?.value,
				event.target?.value,
				match.setValue,
			)
		}

		document.addEventListener('focusin', onDomEvent, true)
		document.addEventListener('input', onDomEvent, true)
		document.addEventListener('change', onDomEvent, true)

		return () => {
			timers.forEach(window.clearTimeout)
			window.clearInterval(intervalId)
			window.clearTimeout(stopIntervalId)
			document.removeEventListener('focusin', onDomEvent, true)
			document.removeEventListener('input', onDomEvent, true)
			document.removeEventListener('change', onDomEvent, true)
		}
	}, [])

	const onAnimationStart = event => {
		if (!String(event.animationName || '').includes('autofill')) return
		const target = event.target
		const match = bindingsRef.current.find(item => item.ref.current === target)
		if (!match) return

		const syncTarget = () => {
			const latest = bindingsRef.current.find(
				item => item.ref.current === target,
			)
			if (!latest) return
			adoptEmptyField(latest.value, target.value, latest.setValue)
		}

		syncTarget()
		// Chrome can apply :-webkit-autofill before the value property is ready.
		scheduleRetries(syncTarget, [0, 50, 100, 250])
	}

	return onAnimationStart
}
